import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { useFacility } from "../../../context/FacilityContext";
import { Drawer, DrawerField, DrawerGroup, drawerInput } from "../Drawer";
import ContactPicker, { toPickerContact } from "../crm/ContactPicker";
import { apiCall, apiGet, fullName, phoneDigits, usePeople } from "../crm/contactsKit";
import { SearchSelect } from "../SearchSelect";
import { PrimaryButton } from "../ui";
import { NO_DATA } from "../../lib/format";
import { loadAppointments, loadDoctors } from "../appointments/appointmentsData";
import { CHANNEL_OPTIONS, SEVERITIES, severityKey } from "./supportKit";

/**
 * Log / edit complaint drawer (design: components/support/ComplaintFormDrawer.tsx).
 *
 * Real data and actions:
 *   contacts to pick from ........ GET /api/contacts (status=active, limit 500), as the old page
 *   new contact .................. POST /api/contacts (first_name, last_name, email, phone, country_code, contact_type, status), as the old page
 *   facilities ................... the facility context (facility_id)
 *   categories ................... GET /api/complaints/categories
 *   assignees .................... GET /api/staff/assignable-users
 *   channel (item 203) ........... channel: whatsapp | phone | in_clinic | email | google_review | ai_front_desk
 *   related doctor (204) ......... doctor_id (users.id), doctors from GET /api/staff?role=doctor at the chosen location
 *   linked appointment (204) ..... appointment_id, from the patient's appointments (GET /api/appointments?patient_id).
 *                                  A contact's patient is found by GET /api/patients?search=<phone> and its contact_id.
 *   log complaint ................ POST /api/complaints { subject, description, category_id, priority, contact_id, facility_id, assigned_to, channel, doctor_id, appointment_id }
 *   edit complaint (205) ......... PUT /api/complaints/:id with the same fields: title, description, contact and
 *                                  location can be changed too
 *
 * Props: open, onClose, initial? ({ contactId, facilityId, subject, description, severity, categoryId, assigneeId }),
 * editing? (toComplaint object), onSaved?(id), olliPrefill?, olliPatientName?, onClearOlli?
 */

const blankContact = { firstName: "", lastName: "", email: "", phone: "" };

export default function ComplaintFormDrawer({ open, onClose, initial, editing, onSaved, olliPrefill = false, olliPatientName = "", onClearOlli }) {
  const fac = useFacility();
  const people = usePeople();
  const [contacts, setContacts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [contactId, setContactId] = useState("");
  const [newContact, setNewContact] = useState(false);
  const [contact, setContact] = useState({ ...blankContact });
  const [location, setLocation] = useState("");
  const [category, setCategory] = useState("");
  const [severity, setSeverity] = useState("Medium");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [assignee, setAssignee] = useState("");
  const [channel, setChannel] = useState("");
  const [doctor, setDoctor] = useState("");
  const [appointment, setAppointment] = useState("");
  const [doctors, setDoctors] = useState([]);
  const [appointments, setAppointments] = useState(null);
  const [errors, setErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState("");

  useEffect(() => {
    if (!open) return;
    apiGet("/api/contacts", { status: "active", limit: 500 }).then((r) => setContacts((r?.data || []).map(toPickerContact)));
    apiGet("/api/complaints/categories").then((r) => setCategories((r?.data || []).filter((c) => c.is_active !== 0 && c.is_active !== false)));
  }, [open]);

  const defaultFacility = fac.currentFacility ? String(fac.currentFacility.id) : fac.facilities[0] ? String(fac.facilities[0].id) : "";
  useEffect(() => {
    if (!open) return;
    const src = editing || initial || {};
    setContactId(src.contactId ? String(src.contactId) : "");
    setNewContact(false);
    setContact({ ...blankContact });
    if (olliPrefill && olliPatientName && !initial?.contactId && !editing?.contactId) {
      const parts = olliPatientName.trim().split(/\s+/);
      setNewContact(true);
      setContact({ ...blankContact, firstName: parts[0] ?? "", lastName: parts.slice(1).join(" ") });
    }
    setLocation(src.facilityId ? String(src.facilityId) : defaultFacility);
    setCategory(src.categoryId ? String(src.categoryId) : "");
    setSeverity(src.severity || "Medium");
    setSubject(src.subject || "");
    setDescription(src.description || "");
    setAssignee(src.assigneeId ? String(src.assigneeId) : "");
    setChannel(src.channel || "");
    setDoctor(src.doctorId ? String(src.doctorId) : "");
    setAppointment(src.appointmentId ? String(src.appointmentId) : "");
    setErrors(false);
    setFailed("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing?.id, initial?.contactId, initial?.subject, initial?.description, olliPrefill, olliPatientName]);

  const activeContact = contacts.find((c) => String(c.id) === contactId) || (editing && contactId && String(editing.contactId) === contactId ? { id: editing.contactId, firstName: editing.name, lastName: "", phone: editing.phone } : null);

  // Doctors at the chosen location (item 204).
  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    loadDoctors(location || undefined).then((list) => live && setDoctors(list || []));
    return () => { live = false; };
  }, [open, location]);

  // The patient's appointments, to link one (item 204). The complaint's own
  // patient when editing; otherwise the patient record that belongs to the
  // chosen contact, found by its phone.
  const activePhone = activeContact?.phone ? phoneDigits(activeContact.phone) : "";
  useEffect(() => {
    if (!open || !contactId) { setAppointments(null); return undefined; }
    let live = true;
    setAppointments(null); // another patient: not the previous patient's appointments while these load
    (async () => {
      let patientId = editing && String(editing.contactId) === contactId ? editing.patientId : null;
      if (!patientId && activePhone.length >= 10) {
        const r = await apiGet("/api/patients", { search: activePhone.slice(-10), limit: 20 });
        patientId = (r?.data?.patients || r?.data || []).find((p) => String(p.contact_id) === contactId)?.id || null;
      }
      const list = patientId ? await loadAppointments({ patientId, order: "DESC", maxPages: 2 }) : [];
      if (live) setAppointments(list || []);
    })();
    return () => { live = false; };
  }, [open, contactId, activePhone, editing]);

  const save = async () => {
    setErrors(true);
    setFailed("");
    const newPhone = contact.phone.replace(/\D/g, "").slice(-10);
    if ((!contactId && !newContact) || (newContact && (!contact.firstName.trim() || newPhone.length !== 10)) || !subject.trim() || !description.trim()) return;
    setSaving(true);
    try {
      let linkedId = contactId ? Number(contactId) : null;
      if (newContact) {
        const duplicate = contacts.find((c) => phoneDigits(c.phone) === newPhone);
        if (duplicate) linkedId = duplicate.id;
        else {
          const r = await apiCall("post", "/api/contacts", { title: "", first_name: contact.firstName.trim(), last_name: contact.lastName.trim(), email: contact.email.trim(), phone: newPhone, country_code: "+91", contact_type: "individual", status: "active" });
          linkedId = r?.data?.id || null;
        }
      }
      if (!linkedId) return;
      const body = {
        subject: subject.trim(),
        description: description.trim(),
        category_id: category ? Number(category) : null,
        priority: severityKey(severity),
        contact_id: linkedId,
        facility_id: location ? Number(location) : null,
        assigned_to: assignee ? Number(assignee) : null,
        channel: channel || null,
        doctor_id: doctor ? Number(doctor) : null,
        appointment_id: appointment ? Number(appointment) : null,
      };
      if (editing) {
        await apiCall("put", `/api/complaints/${editing.id}`, body);
        onSaved?.(editing.id);
      } else {
        const r = await apiCall("post", "/api/complaints", body);
        onSaved?.(r?.data?.id);
      }
      onClose();
    } catch (e) {
      setFailed(e.message);
    } finally {
      setSaving(false);
    }
  };

  const facilityOptions = fac.facilities.map((f) => ({ value: String(f.id), label: f.name }));
  const categoryOptions = categories.map((c) => ({ value: String(c.id), label: c.name }));
  const peopleOptions = people.map((p) => ({ value: String(p.id), label: p.name }));
  const doctorOptions = doctors.map((d) => ({ value: String(d.id), label: d.name }));
  if (doctor && !doctorOptions.some((o) => o.value === doctor)) doctorOptions.unshift({ value: doctor, label: editing?.doctor || "Current doctor" });
  const apptLabel = (a) => [a.code, a.start ? new Date(a.start).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }) : "", a.doctor].filter(Boolean).join(" · ");
  const appointmentOptions = (appointments || []).map((a) => ({ value: String(a.id), label: apptLabel(a) }));
  if (appointment && !appointmentOptions.some((o) => o.value === appointment)) appointmentOptions.unshift({ value: appointment, label: editing?.appointmentDate ? `Linked appointment · ${editing.appointmentDate}${editing.appointmentTime ? ` ${String(editing.appointmentTime).slice(0, 5)}` : ""}` : "Linked appointment" });
  const pickAppointment = (v) => {
    setAppointment(v);
    // The appointment's doctor is the likely related doctor.
    const a = (appointments || []).find((x) => String(x.id) === v);
    if (a?.doctorId && !doctor) setDoctor(String(a.doctorId));
  };
  const field = (label, children, error) => <DrawerField label={label} error={error}>{children}</DrawerField>;

  return (
    <Drawer open={open} onClose={onClose} title={editing ? "Edit complaint" : "Log complaint"} context={editing?.code ?? "Patient support"} width="lg" dirty actions={<PrimaryButton onClick={save} loading={saving}>{editing ? "Save changes" : "Save complaint"}</PrimaryButton>}>
      <div className="flex flex-col gap-5">
        {olliPrefill && <div className="flex items-center justify-between gap-3 rounded-md bg-sky-soft px-3 py-2 text-small text-ink"><span>Filled by Olli. Check the details before you save.</span><button type="button" onClick={onClearOlli} className="shrink-0 text-small font-semibold text-sky-deep">Clear</button></div>}
        <DrawerGroup title="Patient" hint="Search by name or phone. New people are saved as a contact.">
          {activeContact ? (
            <div className="flex items-center justify-between rounded-sm bg-mist px-4 py-3">
              <div><p className="text-small font-semibold">{fullName(activeContact) || "Patient"}</p><p className="text-caption text-muted">{activeContact.phone ? `+91 ${phoneDigits(activeContact.phone)}` : NO_DATA}</p></div>
              <div className="flex items-center gap-3"><Link to={`/crm/contacts/${activeContact.id}`} className="text-caption font-semibold text-sky-deep underline">Open contact</Link><button type="button" onClick={() => { setContactId(""); setAppointment(""); }} className="text-caption font-semibold text-muted underline">Change</button></div>
            </div>
          ) : newContact ? (
            <div className="grid grid-cols-2 gap-3">
              <DrawerField label="First name"><input className={drawerInput} value={contact.firstName} onChange={(e) => setContact({ ...contact, firstName: e.target.value })} /></DrawerField>
              <DrawerField label="Last name"><input className={drawerInput} value={contact.lastName} onChange={(e) => setContact({ ...contact, lastName: e.target.value })} /></DrawerField>
              <DrawerField label="Phone" error={errors && contact.phone.replace(/\D/g, "").length !== 10 ? "Enter a 10-digit number." : undefined}><div className="flex h-11 rounded-sm border border-line bg-paper"><span className="flex items-center border-r border-line px-3 text-small text-muted">+91</span><input inputMode="numeric" className="min-w-0 flex-1 bg-transparent px-3 text-small outline-none" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} /></div></DrawerField>
              <DrawerField label="Email"><input type="email" className={drawerInput} value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} /></DrawerField>
              <button type="button" onClick={() => setNewContact(false)} className="col-span-2 text-left text-caption font-semibold text-sky-deep underline">Choose an existing contact instead</button>
            </div>
          ) : (
            <>
              <ContactPicker contacts={contacts} leads={null} onSelect={(c) => setContactId(String(c.id))} onCreateNew={(query) => { setNewContact(true); const digits = query.replace(/\D/g, ""); setContact({ ...blankContact, ...(digits.length >= 10 ? { phone: digits.slice(-10) } : { firstName: query.split(/\s+/)[0] ?? "", lastName: query.split(/\s+/).slice(1).join(" ") }) }); }} />
              {errors && !contactId && <p className="text-caption text-danger">Choose or create a contact.</p>}
              <button type="button" onClick={() => setNewContact(true)} className="flex items-center gap-1 text-caption font-semibold text-sky-deep"><Plus size={14} />New contact</button>
            </>
          )}
        </DrawerGroup>
        <DrawerGroup title="Complaint">
          <div className="col-span-2">{field("Facility", <SearchSelect value={location} options={facilityOptions} onChange={setLocation} searchable />)}</div>
          <div className="col-span-2">{field("Title", <input className={drawerInput} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="For example, Long wait before my appointment" />, errors && !subject.trim() ? "Add a title." : undefined)}</div>
          <div className="col-span-2">{field("Description", <textarea rows={4} className={`${drawerInput} h-auto py-3`} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What happened? What outcome is the patient expecting?" />, errors && !description.trim() ? "Add a description." : undefined)}</div>
          {field("Category", <SearchSelect value={category} options={categoryOptions} onChange={setCategory} placeholder={NO_DATA} />)}
          {field("Priority", <SearchSelect value={severity} options={SEVERITIES} onChange={setSeverity} />)}
        </DrawerGroup>
        <DrawerGroup title="More details" hint="Optional">
          {field("Channel", <SearchSelect value={channel} options={[{ value: "", label: "Not set" }, ...CHANNEL_OPTIONS]} onChange={setChannel} placeholder="Not set" />)}
          {field("Related doctor", <SearchSelect value={doctor} options={[{ value: "", label: "None" }, ...doctorOptions]} onChange={setDoctor} placeholder="None" searchable />)}
          <div className="col-span-2">{field("Linked appointment", <SearchSelect value={appointment} options={[{ value: "", label: "None" }, ...appointmentOptions]} onChange={pickAppointment} placeholder={!contactId ? "Choose the patient first" : appointments === null ? "Loading appointments…" : appointmentOptions.length ? "None" : "No appointments for this patient"} disabled={!contactId || (appointments !== null && !appointmentOptions.length && !appointment)} searchable />)}</div>

          {field("Assignee", <SearchSelect value={assignee} options={peopleOptions} onChange={setAssignee} placeholder="Unassigned" searchable />)}
        </DrawerGroup>
        {failed && <p role="alert" className="text-caption text-danger">{failed}</p>}
      </div>
    </Drawer>
  );
}
